import { Menu as MenuIcon } from '@mui/icons-material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CloseIcon from '@mui/icons-material/Close';
import HandymanIcon from '@mui/icons-material/Handyman';
import { Box, Button, Chip, Drawer, IconButton, Tooltip } from '@mui/material';
import { isProduction } from '@pn/core/utils/env';
import { useAuthenticationService } from '@pn/services/authentication';
import { AdminModeToggleButton } from '@pn/ui/custom-components/AdminModeToggleButton';
import { BeamerButton } from '@pn/ui/support/BeamerButton';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LightDarkModeToggleButton } from 'src/ui/components/LightDarkModeToggleButton';
import { useUI } from 'src/ui/contexts/useUIState';
import { NavDrawerContent } from 'src/ui/navigation/NavDrawerContent';
import { makeStyles } from 'tss-react/mui';
import { AppTitle } from './AppTitle';
import { AuthButton } from './AuthButton';

const useStyles = makeStyles()((theme) => ({
  header: {
    gridArea: 'header',
  },
  menuIcon: {
    mr: 2,
  },
  appBar: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    position: 'sticky',
    top: 0,
    zIndex: 100,
    backgroundColor: theme.palette.background.paper,
  },
  lightDarkModeToggleButtonContainer: {
    display: 'inline-block',
    position: 'relative',
    bottom: 1,
  },
}));

export const AppHeader = React.memo(function AppHeader() {
  const { classes } = useStyles();
  const {
    isMobileDrawerOpen,
    isToolsPanelOpen,
    handleToggleToolsPanel,
    handleToggleDrawer,
    handleCloseDrawer,
  } = useUI();
  const { isAuthenticated } = useAuthenticationService();
  const navigate = useNavigate();

  return (
    <>
      <Box
        className={classes.appBar}
        sx={{
          gridArea: 'header',
          flexGrow: 1,
          alignItems: 'center',
          '@media print': { display: 'none' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 1,
            pr: 0,
            mx: 1,
          }}
        >
          <Box
            component="span"
            sx={(theme) => ({
              [theme.breakpoints.up('lg')]: {
                display: 'none',
              },
            })}
          >
            <IconButton
              size="small"
              edge="start"
              color="inherit"
              aria-label="menu"
              className={classes.menuIcon}
              onClick={handleToggleDrawer}
            >
              <MenuIcon />
            </IconButton>
          </Box>

          <Box
            component={'span'}
            sx={[
              {
                display: 'flex',
              },
              (theme) => ({
                [theme.breakpoints.down('lg')]: {
                  display: 'none',
                },
              }),
            ]}
          >
            <AppTitle />
          </Box>

          <Box sx={{ flexGrow: 1 }} />
          <AdminModeToggleButton />

          {!isProduction() && (
            <Box
              sx={{
                mr: 1,
              }}
            >
              <Chip
                title="You are in the staging environment"
                color={'warning'}
                label="WIP Preview"
              />
            </Box>
          )}
          <Button
            onClick={handleToggleToolsPanel}
            size="small"
            variant="contained"
            endIcon={isToolsPanelOpen ? <CloseIcon /> : <HandymanIcon />}
          >
            Tools
          </Button>
          <AuthButton />

          <Box sx={{ mx: 1 }}>
            <BeamerButton configId={'intel'} />
          </Box>
          {isAuthenticated && (
            <Box
              sx={{
                mr: 1,
                display: 'inline-block',
              }}
            >
              <Tooltip title="My account">
                <IconButton
                  aria-label="account"
                  onClick={() => navigate('/account')}
                >
                  <AccountCircleIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}

          <Box className={classes.lightDarkModeToggleButtonContainer}>
            <LightDarkModeToggleButton />
          </Box>
        </Box>
      </Box>
      <Drawer
        anchor="left"
        open={isMobileDrawerOpen}
        onClose={handleCloseDrawer}
      >
        <NavDrawerContent />
      </Drawer>
    </>
  );
});
